/**
 * Dialog Component
 * 
 * Modal dialog using native HTML dialog element for accessibility.
 * Per design.md: Premium glass effect, semantic tokens.
 */

import * as React from 'react';
import { cn } from '../../lib/utils';
import { X } from 'lucide-react';

interface DialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    children: React.ReactNode;
}

interface DialogContentProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
}

interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
}

interface DialogTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
    children: React.ReactNode;
}

interface DialogDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
    children: React.ReactNode;
}

interface DialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
}

function Dialog({ open, onOpenChange, children }: DialogProps) {
    const dialogRef = React.useRef<HTMLDialogElement>(null);

    React.useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;

        if (open) {
            dialog.showModal();
        } else {
            dialog.close();
        }
    }, [open]);

    return (
        <dialog
            ref={dialogRef}
            style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                margin: 0,
                padding: 0,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                maxWidth: '90vw',
                maxHeight: '90vh',
                zIndex: 50,
                overflow: 'visible',
            }}
            className="backdrop:bg-black/60 backdrop:backdrop-blur-sm"
            onClose={() => onOpenChange(false)}
        >
            {children}
        </dialog>
    );
}

function DialogContent({ className, children, ...props }: DialogContentProps) {
    return (
        <div
            className={cn(
                'relative bg-card rounded-lg shadow-xl',
                'border border-border',
                'w-full max-w-lg p-6',
                className
            )}
            {...props}
        >
            {children}
        </div>
    );
}

function DialogHeader({ className, children, ...props }: DialogHeaderProps) {
    return (
        <div
            className={cn('flex flex-col space-y-1.5 mb-4', className)}
            {...props}
        >
            {children}
        </div>
    );
}

function DialogTitle({ className, children, ...props }: DialogTitleProps) {
    return (
        <h2
            className={cn('text-lg font-semibold text-foreground', className)}
            {...props}
        >
            {children}
        </h2>
    );
}

function DialogDescription({ className, children, ...props }: DialogDescriptionProps) {
    return (
        <p
            className={cn('text-sm text-muted-foreground whitespace-pre-line', className)}
            {...props}
        >
            {children}
        </p>
    );
}

function DialogFooter({ className, children, ...props }: DialogFooterProps) {
    return (
        <div
            className={cn(
                'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6',
                className
            )}
            {...props}
        >
            {children}
        </div>
    );
}

function DialogClose({
    onClick,
    className
}: {
    onClick?: () => void;
    className?: string
}) {
    return (
        <button
            type="button"
            className={cn(
                'absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background',
                'transition-opacity hover:opacity-100',
                'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                'cursor-pointer',
                className
            )}
            onClick={onClick}
            aria-label="Close"
        >
            <X className="h-4 w-4" />
        </button>
    );
}

export {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    DialogClose,
};
